// src/components/business-rules/EmailRemetenteSection.tsx
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { emailService } from '@/services/emailService';
import type { Client } from '@/types/client';
import type { EmailSenderMode, RemetenteConfigResponse } from '@/types/email';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ClientEmailDomainSection from './ClientEmailDomainSection';

interface Props {
  client: Client;
}

export default function EmailRemetenteSection({ client }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const clientId = client.id;
  const queryKey = ['email-remetente', clientId];

  const { data, isLoading, isError } = useQuery<RemetenteConfigResponse | null>({
    queryKey,
    queryFn: () => emailService.getRemetente(clientId),
    enabled: !!clientId,
    refetchOnWindowFocus: false, // não sobrescrever edição em andamento num refetch de foco
  });

  const [mode, setMode] = useState<EmailSenderMode>('GLOBAL');
  const [fromName, setFromName] = useState('');
  const [fromLocalPart, setFromLocalPart] = useState('');
  const [replyTo, setReplyTo] = useState('');

  // Sincroniza os campos quando a config chega do servidor.
  useEffect(() => {
    if (!data) return;
    setMode(data.mode);
    setFromName(data.config.from_name ?? '');
    setFromLocalPart(data.config.from_local_part ?? '');
    setReplyTo(data.config.reply_to ?? '');
  }, [data]);

  const dominioGlobal = data?.dominio_global ?? 'mail.atomos.tech';

  const salvar = useMutation({
    mutationFn: (novoMode: EmailSenderMode) =>
      emailService.setRemetente(clientId, {
        mode: novoMode,
        from_name: fromName || null,
        from_local_part: fromLocalPart || null,
        reply_to: replyTo || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Remetente salvo', description: 'A configuração de envio foi atualizada.' });
    },
    onError: () => toast({ title: 'Erro', description: 'Falha ao salvar o remetente.', variant: 'destructive' }),
  });

  // Trocar o modo grava imediatamente (preserva os campos já digitados).
  // Só persiste se a config já carregou — evita gravar nulls sobre uma config existente.
  const trocarModo = (novoMode: EmailSenderMode) => {
    setMode(novoMode);
    if (!data) return;
    salvar.mutate(novoMode);
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando configuração de remetente...</p>;
  }

  if (isError) {
    return <p className="text-sm text-destructive">Erro ao carregar a configuração de remetente.</p>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Remetente de email</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Domínio de envio</Label>
            <div className="flex gap-2 mt-1">
              <Button
                type="button"
                size="sm"
                variant={mode === 'GLOBAL' ? 'default' : 'outline'}
                disabled={salvar.isPending}
                onClick={() => trocarModo('GLOBAL')}
              >
                Domínio global
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === 'OWN' ? 'default' : 'outline'}
                disabled={salvar.isPending}
                onClick={() => trocarModo('OWN')}
              >
                Domínio próprio
              </Button>
            </div>
          </div>

          {mode === 'GLOBAL' && (
            <div className="space-y-4 border-t pt-4">
              <div>
                <Label htmlFor="r-fromname">Nome do remetente</Label>
                <Input id="r-fromname" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Cobrança Empresa X" />
              </div>
              <div>
                <Label htmlFor="r-localpart">Remetente</Label>
                <div className="flex items-center gap-2">
                  <Input id="r-localpart" value={fromLocalPart} onChange={(e) => setFromLocalPart(e.target.value)} placeholder="empresax" />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">@{dominioGlobal}</span>
                </div>
              </div>
              <div>
                <Label htmlFor="r-replyto">Reply-To</Label>
                <Input id="r-replyto" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="financeiro@empresax.com.br" />
              </div>
              <Button size="sm" onClick={() => salvar.mutate('GLOBAL')} disabled={salvar.isPending}>
                {salvar.isPending ? 'Salvando...' : 'Salvar remetente'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {mode === 'OWN' && <ClientEmailDomainSection client={client} />}
    </div>
  );
}
